import { InjectionToken } from '@angular/core';
import { interval } from 'rxjs';
import { filter, finalize, map, take, timeout } from 'rxjs/operators';
import isUndefined from 'lodash-es/isUndefined';

import { WindowRef } from '@finder/shared/services/window-ref/window-ref.service';
import { DebugLogService } from '@finder/shared/services/debug-log/debug-log.service';
import { OneIdInstance } from '@finder/shared/services/one-id/one-id.interface';
import { OneIdService } from '@finder/shared/services/one-id/one-id.service';
import { timeout as rxjsTimeout } from '@finder/shared/utils/time/timeout';
import { ERROR, INIT, oneIdPlaceholder } from './one-id.constants';
import { SsrService } from '../ssr/ssr.service';

const logName = 'OneID Factory';
let timerStart;

const pollOneId = (
    windowRef: WindowRef,
    debugLog: DebugLogService,
    resolveOneIdService: (oid) => void,
) => {
    const window = windowRef.nativeWindow;

    // Create a dummy
    let oid: OneIdInstance = oneIdPlaceholder();
    let intervalIndex = 0;

    // We want to get the global singleton from `window` but we don't know when it will be
    // available so keep trying. If it takes 2 seconds just quit and return the dummy instance.
    // TODO: change this to throw rather than return the dummy and handle it in the UI component
    interval(250) // Check every quarter-second
        .pipe(
            map(() => {
                debugLog.log(`***** ${logName} checking: ${++intervalIndex}`);

                /**
                 * https://mywiki.disney.com/pages/viewpage.action?spaceKey=gamprofile&title=Profile+Authenticator
                 *
                 * In the future we might be able to simplify this check to
                 * window.PEP.PROFILE_AUTH but hold off for now until
                 * we get a stable V4.
                 */
                let timerFound = Date.now();
                let versionFound;
                if (window.OneID) { // Check for v4 first
                    versionFound = 'v4';
                } else if (window.DisneyID) { // Check for v2
                    versionFound = 'v2';
                }

                if (versionFound) {
                    debugLog.log(`***** ${logName}: found ${versionFound} in ${(timerFound - timerStart) / 1000}ms`);
                }

                return { oid: window.OneID || window.DisneyID };
            }),
            filter(val => !isUndefined(val.oid)),
            take(1),
            // Timeout triggers the error case in the subscriber
            timeout(10000), // Timeout after 2 seconds
            finalize(() => resolveOneIdService(oid)),
        )
        .subscribe({
            next: (result) => {
                oid = result.oid.get() as OneIdInstance;
            },
            error: () => {
                debugLog.log(`***** ${logName} - DisneyID not found on global namespace`);
            }
        });
};

const oneIdEventListener = (
    windowRef: WindowRef,
    debugLog: DebugLogService,
    resolveOneIdService: (oid) => void,
) => {
    debugLog.log(`***** oneIdEventListener start: ${Date.now()}`);

    const window = windowRef.nativeWindow;
    window.document.addEventListener('navigation', (event: any) => {
        if (event.detail.event === 'oneId-init') {
            const timerInit = Date.now();
            debugLog.log(`***** oneIdEventListener initialized: ${timerInit} - ${(timerInit - timerStart) / 1000}s`);
            const oid = window.PEP.PROFILE_AUTH['_did'];
            resolveOneIdService(oid);
        } else {
            debugLog.log(`***** oneIdEventListener event: ${event.detail.event} - ${Date.now()}`);
            console.log('Other one id event', event.detail.event);
        }
    });
};

const observeListener = (
    windowRef: WindowRef,
    debugLog: DebugLogService,
    resolveOneIdService: (oid) => void,
) => {
    const RESOLVED = '_resolved';

    // If the service never resolves we need to timeout and move on
    const rxjsTimeout$ = rxjsTimeout(() => {
        resolveOneIdService(oneIdPlaceholder());
    }, 3000);

    const resolveService = (privateName) => resolveOneIdService(windowRef.nativeWindow[privateName].get());
    const buildWatcher = (privateName) => {
        return {
            get: function() {
                return this[privateName];
            },
            set: function(val) {
                if (this[RESOLVED]) {
                    return;
                }

                this[privateName] = val;
                this[RESOLVED] = privateName;
                let timerFound = Date.now();

                debugLog.log(`***** ${logName}: found ${privateName} in ${(timerFound - timerStart) / 1000}ms`);

                // Clear our timer
                rxjsTimeout$.unsubscribe();

                // Resolve the service
                setTimeout(() => resolveService(privateName), 0);
            }
        };
    };

    Object.defineProperties(windowRef.nativeWindow, {
        OneID: buildWatcher('_oneId'),
        DisneyID: buildWatcher('_disneyId'),
    });
};

// The injected values are defined in app.config
export const OneIdFactory = async (
    windowRef: WindowRef,
    debugLog: DebugLogService,
    ssrService: SsrService,
) => new Promise((resolve, reject) => {
    timerStart = Date.now();
    debugLog.log(`***** ${logName} start: ${timerStart}`);

    const resolveOneIdService = (oid: OneIdInstance) => {
        const oneIdService = new OneIdService(oid, debugLog);
        const rxjsTimeout$ = rxjsTimeout(() => {
            resolveService(null);
        }, 2000);

        const resolveService = (obj) => {
            // Cancel the timer
            rxjsTimeout$.unsubscribe();

            resolve(oneIdService);

            oid.off(INIT, resolveService);
            oid.off(ERROR, resolveService);
        };

        debugLog.log(`***** ${logName} finalize global scope`, oid, oneIdService);
        /**
         * Wait for the service to init
         *
         * app.config calls this factory when the page is loading. So we're always using this factory. We
         * need to make sure this finishes or the page will never load if something really weird happens
         * with OneID.
         */
        oid.on(INIT, resolveService);
        oid.on(ERROR, resolveService);
    };

    // @SSR: Resolve right away if on the server
    if (ssrService.isSSR()) {
        resolveOneIdService(oneIdPlaceholder());

        return;
    }

    // pollOneId(windowRef, debugLog, resolveOneIdService); // Uncomment to use the polling method
    // oneIdEventListener(windowRef, debugLog, resolveOneIdService); // Uncomment to use the event listener method
    observeListener(windowRef, debugLog, resolveOneIdService);
});

export const ONE_ID_INSTANCE =
    new InjectionToken('ONE_ID_INSTANCE', {
        factory: (): Partial<OneIdInstance> => ({})
    });

